<template>
  <a-modal
    :title="title"
    :width="660"
    :visible="visible"
    :confirmLoading="loading"
    @cancel="handleCancel"
    :footer="null"
    :wrapClassName="modalWrapClass"
  >
    <a-spin :spinning="loading">
      <div v-if="task" class="task-detail-container">
        <!-- 头部核心状态区 -->
        <div class="header-status-bar">
          <div class="header-left">
            <a-icon type="schedule" class="header-icon" />
            <span class="task-title">{{ task.title || '运维周期任务' }}</span>
          </div>
          <div class="task-tag">
            <span :class="['status-chip', getStatusChipClass(task.status)]">
              <span class="status-dot"></span>
              {{ getStatusText(task.status) }}
            </span>
          </div>
        </div>

        <a-alert v-if="task.paused" :message="task.pauseReason" type="warning" show-icon />
        <div class="divider-line"></div>

        <!-- 现代化属性网格面板 -->
        <div class="info-card-grid">
          <div class="grid-item">
            <span class="item-label"><a-icon type="number" style="margin-right: 4px;" />任务编号</span>
            <span class="item-value task-id-text">#{{ task.id }}</span>
          </div>
          <div class="grid-item">
            <span class="item-label"><a-icon type="environment" style="margin-right: 4px;" />所属站点</span>
            <span class="item-value">{{ task.siteName || '-' }}</span>
          </div>

          <div class="grid-item">
            <span class="item-label"><a-icon type="appstore" style="margin-right: 4px;" />业务类型</span>
            <span class="item-value business-text">{{ task.businessName || task.businessType || '-' }}</span>
          </div>
          <div class="grid-item">
            <span class="item-label"><a-icon type="aim" style="margin-right: 4px;" />执行范围</span>
            <span class="item-value">{{ task.scopeLabel || task.scopeKey || '-' }}</span>
          </div>

          <div class="grid-item full-row">
            <span class="item-label"><a-icon type="clock-circle" style="margin-right: 4px;" />到期时间</span>
            <div class="item-value flex-align">
              <span :class="['due-time-text', { 'overdue': isOverdue(task) }]">
                {{ task.dueAt || '-' }}
              </span>
              <a-tag v-if="isOverdue(task)" color="red" class="overdue-tag">已逾期</a-tag>
            </div>
          </div>

          <div class="grid-item full-row">
            <span class="item-label"><a-icon type="team" style="margin-right: 4px;" />负责角色与人员</span>
            <div class="item-value member-box">
              <template v-if="task.members && task.members.length > 0">
                <a-tag v-for="member in task.members" :key="member.userId" color="blue" class="user-chip">
                  <a-icon type="user" style="margin-right: 3px;" />{{ member.name || member.username }}
                </a-tag>
              </template>
              <span v-else class="empty-member-tip">暂未匹配到执行人员（待分配）</span>
            </div>
          </div>

          <template v-if="task.status === 'DONE'">
            <div class="grid-item">
              <span class="item-label"><a-icon type="check-circle" style="margin-right: 4px;" />完成人员</span>
              <span class="item-value">{{ task.completedByName || task.completedBy || '-' }}</span>
            </div>
            <div class="grid-item">
              <span class="item-label"><a-icon type="carry-out" style="margin-right: 4px;" />完成时间</span>
              <span class="item-value">{{ task.completedAt || '-' }}</span>
            </div>
          </template>
        </div>

        <!-- 动作引导提示条 -->
        <div v-if="task.action && task.action.tip" class="custom-alert-box">
          <a-icon type="info-circle" theme="filled" class="alert-icon" />
          <span class="alert-content">{{ task.action.tip }}</span>
        </div>

        <!-- 底部操作按钮 -->
        <div class="action-footer-bar">
          <a-button class="btn-cancel" @click="handleCancel">关 闭</a-button>
          <a-button
            v-if="task.action && task.action.actionCode !== 'NONE' && (task.action.canView || task.action.canCreate || task.action.canContinue)"
            type="primary"
            icon="search"
            class="btn-action"
            @click="handleBusinessAction"
          >
            {{ task.action.actionName || '查看业务记录' }}
          </a-button>
        </div>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import { getAction } from '@/api/manage'
import { openMaintenanceAction } from '../maintenanceBusinessActions'

export default {
  name: 'MaintenanceTaskModal',
  data() {
    return {
      title: '运维任务详情',
      visible: false,
      loading: false,
      task: null,
      taskId: null
    }
  },
  computed: {
    modalWrapClass() {
      return this.$store.state.bgLight ? 'lightColor' : 'darkColor'
    }
  },
  methods: {
    show(taskId) {
      this.taskId = taskId
      this.visible = true
      this.task = null
      this.loadTaskDetail()
    },
    loadTaskDetail() {
      if (!this.taskId) return
      this.loading = true
      const requestedId = this.taskId
      getAction(`/om/maintenance/tasks/${requestedId}?terminal=WEB`).then(res => {
        if (!this.visible || this.taskId !== requestedId) return
        if (res.success && res.result) {
          this.task = res.result
        } else {
          this.$message.error(res.message || '获取任务详情失败')
        }
      }).catch(err => {
        this.$message.error('网络请求异常')
      }).finally(() => {
        this.loading = false
      })
    },
    getStatusChipClass(status) {
      if (status === 'OPEN') {
        return this.isOverdue(this.task) ? 'chip-danger' : 'chip-warning'
      }
      if (status === 'DONE') return 'chip-success'
      if (status === 'REVOKED') return 'chip-purple'
      if (status === 'CANCELLED') return 'chip-default'
      return 'chip-info'
    },
    getStatusText(status) {
      switch (status) {
        case 'OPEN':
          return this.isOverdue(this.task) ? '已逾期' : '待完成'
        case 'DONE':
          return '已完成'
        case 'REVOKED':
          return '已撤销'
        case 'CANCELLED':
          return '已取消'
        default:
          return status || '待完成'
      }
    },
    isOverdue(task) {
      if (!task || task.status !== 'OPEN' || !task.dueAt) return false
      return new Date(task.dueAt).getTime() < new Date().getTime()
    },
    async handleBusinessAction() {
      if (!this.task || !this.task.action) return
      if (await openMaintenanceAction(this.task.action, this.task.businessType, this)) this.visible = false
    },
    handleCancel() {
      this.visible = false
    }
  }
}
</script>

<style lang="less" scoped>
.task-detail-container {
  padding: 6px 4px;
}

/* 头部区 */
.header-status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-left {
    display: flex;
    align-items: center;
    .header-icon {
      font-size: 18px;
      color: #40a9ff;
      margin-right: 8px;
    }
    .task-title {
      font-size: 16px;
      font-weight: 600;
      color: #ffffff;
    }
  }
}

/* 状态胶囊 */
.status-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 6px;
  }

  &.chip-warning {
    background: rgba(250, 140, 22, 0.2);
    border: 1px solid rgba(250, 140, 22, 0.45);
    color: #ffa940;
    .status-dot { background: #ffa940; }
  }
  &.chip-danger {
    background: rgba(245, 34, 45, 0.2);
    border: 1px solid rgba(245, 34, 45, 0.5);
    color: #ff7875;
    .status-dot { background: #ff7875; }
  }
  &.chip-success {
    background: rgba(82, 196, 26, 0.2);
    border: 1px solid rgba(82, 196, 26, 0.45);
    color: #73d13d;
    .status-dot { background: #73d13d; }
  }
  &.chip-purple {
    background: rgba(114, 46, 209, 0.2);
    border: 1px solid rgba(114, 46, 209, 0.45);
    color: #b37feb;
    .status-dot { background: #b37feb; }
  }
  &.chip-default {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.65);
    .status-dot { background: rgba(255, 255, 255, 0.65); }
  }
}

.divider-line {
  height: 1px;
  background: rgba(255, 255, 255, 0.12);
  margin: 14px 0 16px 0;
}

/* 属性卡片网格 */
.info-card-grid {
  background: rgba(14, 39, 82, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 14px;
  column-gap: 20px;

  .grid-item {
    display: flex;
    flex-direction: column;

    &.full-row {
      grid-column: 1 / -1;
    }

    .item-label {
      color: #8daed8;
      font-size: 13px;
      margin-bottom: 4px;
      display: flex;
      align-items: center;
    }

    .item-value {
      color: #ffffff;
      font-size: 14px;
      font-weight: 500;
      word-break: break-all;

      &.task-id-text {
        color: #40a9ff;
        font-family: monospace;
        font-size: 15px;
      }

      &.business-text {
        color: #69c0ff;
      }

      .due-time-text {
        color: #ffffff;
        &.overdue {
          color: #ff7875;
          font-weight: 600;
        }
      }

      .overdue-tag {
        margin-left: 8px;
        line-height: 18px;
        height: 20px;
        padding: 0 6px;
      }
    }

    .flex-align {
      display: flex;
      align-items: center;
    }

    .member-box {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 2px;

      .user-chip {
        background: rgba(24, 144, 255, 0.2);
        border: 1px solid rgba(24, 144, 255, 0.45);
        color: #bae7ff;
        padding: 2px 8px;
        border-radius: 4px;
        margin: 0;
      }

      .empty-member-tip {
        color: rgba(255, 255, 255, 0.45);
        font-size: 13px;
        font-style: italic;
      }
    }
  }
}

/* 提示条 */
.custom-alert-box {
  margin-top: 16px;
  padding: 10px 14px;
  background: rgba(24, 144, 255, 0.12);
  border: 1px solid rgba(24, 144, 255, 0.35);
  border-radius: 6px;
  display: flex;
  align-items: center;

  .alert-icon {
    color: #40a9ff;
    font-size: 16px;
    margin-right: 8px;
    flex-shrink: 0;
  }

  .alert-content {
    color: #bae7ff;
    font-size: 13px;
    line-height: 1.5;
  }
}

/* 底部按钮栏 */
.action-footer-bar {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;

  .btn-cancel {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.25);
    color: #ffffff;
    border-radius: 4px;
    padding: 0 16px;
    height: 32px;

    &:hover {
      background: rgba(255, 255, 255, 0.16);
      border-color: #ffffff;
      color: #ffffff;
    }
  }

  .btn-action {
    background: #1890ff;
    border-color: #1890ff;
    border-radius: 4px;
    height: 32px;
    padding: 0 16px;
    font-weight: 500;
  }
}

/* 浅色主题适配 */
.lightColor {
  .header-status-bar .header-left .task-title {
    color: rgba(0, 0, 0, 0.85);
  }
  .divider-line {
    background: #e8e8e8;
  }
  .info-card-grid {
    background: #f8fafd;
    border-color: #dbe4ee;

    .grid-item {
      .item-label {
        color: #64748b;
      }
      .item-value {
        color: #1e293b;
        &.task-id-text {
          color: #096dd9;
        }
        &.business-text {
          color: #1677ff;
        }
        .due-time-text {
          color: #1e293b;
          &.overdue {
            color: #cf1322;
          }
        }
      }
      .member-box {
        .user-chip {
          background: #e6f7ff;
          border-color: #91d5ff;
          color: #096dd9;
        }
        .empty-member-tip {
          color: #94a3b8;
        }
      }
    }
  }
  .custom-alert-box {
    background: #e6f7ff;
    border-color: #91d5ff;
    .alert-icon {
      color: #1890ff;
    }
    .alert-content {
      color: #0050b3;
    }
  }
  .action-footer-bar {
    .btn-cancel {
      background: #ffffff;
      border-color: #d9d9d9;
      color: rgba(0, 0, 0, 0.65);
      &:hover {
        border-color: #40a9ff;
        color: #40a9ff;
      }
    }
  }
}
</style>
